import { LitElement, html, css, unsafeCSS } from 'lit';
import { api } from '../api.js';
import { confirmDialog, alertDialog } from '../utils/confirm-dialog.js';
import { EXTRACTION_PROFILES, normalizeExtractionProfile } from '@mv/models';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { Info, ChevronDown, Eye } from 'lucide';
import { lucideIcon } from '../icon.js';
import { tipMove, tipHide } from '../utils/hover-tip.js';

/** De taalcodes met een leesbare naam; de codes zelf komen van de server. */
const TAALNAAM = { nl: 'Nederlands', en: 'Engels', de: 'Duits', fr: 'Frans', es: 'Spaans' };

const CHEVRON = "url(\"data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='14'%20height='14'%20viewBox='0%200%2024%2024'%20fill='none'%20stroke='%236c757d'%20stroke-width='2.5'%20stroke-linecap='round'%20stroke-linejoin='round'%3E%3Cpolyline%20points='6%209%2012%2015%2018%209'/%3E%3C/svg%3E\")";

/**
 * Production AI config (/admin/settings/model). Sysadmin selects the transcription
 * model, extraction model, and ACTIVE prompt version used for ALL users at /projects.
 * Saving requires a double confirmation. The active prompt itself is immutable — to
 * change it you create/select a different version (edited on the validation side).
 */
/** De redeneerstanden van een GPT-6-model, in gewone woorden. */
const REDENEERSTAND = {
  none: 'Geen (snelst, goedkoopst)',
  low: 'Laag (aanbevolen)',
  medium: 'Gemiddeld',
  high: 'Hoog (traagst, duurst)',
};

/** Versienummer uit een titel als 'V0.12 (…)' → [0, 12]; null zonder nummer. */
function versieVan(titel) {
  const m = /^\s*V(\d+)\.(\d+)/i.exec(String(titel || ''));
  return m ? [Number(m[1]), Number(m[2])] : null;
}
/** > 0 als titel a een hoger versienummer heeft dan b (een titel zonder nummer telt niet). */
function vergelijkVersie(a, b) {
  const va = versieVan(a), vb = versieVan(b);
  if (!va) return -1;
  if (!vb) return 1;
  return va[0] - vb[0] || va[1] - vb[1];
}

export class AdminModelSettings extends LitElement {
  static properties = {
    _options: { state: true },
    _config: { state: true },
    _versions: { state: true },
    _draft: { state: true },
    _loading: { state: true },
    _saving: { state: true },
    _prompts: { state: true },  // de prompts van Vooronderzoek en Flip (pas bij het eerste openen opgehaald)
    _viewer: { state: true },   // wat het leesvenster toont, of null
    _open: { state: true },     // welke AI-onderdelen zijn opengeklapt (Set)
  };

  constructor() {
    super();
    this._options = { transcriptionModels: [], extractionModels: [], transcriptLanguages: [] };
    this._config = null;
    this._versions = [];
    this._draft = null;
    this._loading = true;
    this._saving = false;
    this._viewer = null;
    this._open = new Set();
  }

  connectedCallback() { super.connectedCallback(); this._load(); }

  async _load() {
    this._loading = true;
    try {
      const [opt, cfg, pv] = await Promise.all([api.getModelOptions(), api.getModelConfig(), api.listPromptVersions()]);
      this._options = opt;
      this._config = cfg;
      this._versions = pv.versions || [];
      this._draft = { ...cfg };
    } catch (e) {
      alertDialog({ title: 'Fout', message: `Config laden mislukt: ${e.message}` });
    } finally {
      this._loading = false;
    }
  }

  get _dirty() {
    const a = this._draft, b = this._config;
    if (!a || !b) return false;
    if (a.transcriptionModel !== b.transcriptionModel || a.extractionModel !== b.extractionModel) return true;
    if ((a.reasoningEffort || 'low') !== (b.reasoningEffort || 'low')) return true;
    if ((a.fieldOrderModel || 'gpt-4.1') !== (b.fieldOrderModel || 'gpt-4.1')) return true;
    if ((a.labAssistantModel || 'gpt-4.1') !== (b.labAssistantModel || 'gpt-4.1')) return true;
    if ((a.transcriptLanguages || []).join(',') !== (b.transcriptLanguages || []).join(',')) return true;
    if (!!a.speechCheck !== !!b.speechCheck) return true;
    if (!!a.compactOutput !== !!b.compactOutput || !!a.priority !== !!b.priority) return true;
    const ta = a.transcriptionPromptVersionIds || {}, tb = b.transcriptionPromptVersionIds || {};
    if (EXTRACTION_PROFILES.some(p => (ta[p.id] || '') !== (tb[p.id] || ''))) return true;
    const pa = a.promptVersionIds || {}, pb = b.promptVersionIds || {};
    return EXTRACTION_PROFILES.some(p => pa[p.id] !== pb[p.id]);
  }

  _set(field, value) { this._draft = { ...this._draft, [field]: value }; }

  /**
   * Het leesvenster. Twee bronnen: een promptversie uit de database (woordenlijst of
   * extractieprompt, via het oogje in de tabel) of een prompt uit de code (Vooronderzoek, Flip).
   */
  async _bekijkVersie(id, soort) {
    if (!id) return;
    try {
      const v = await api.getPromptVersion(id);
      this._viewer = {
        title: v.title,
        sub: `${soort}. Bewerken en nieuwe versies maken gaat op de validatiepagina van dat model.`,
        blokken: [{ label: soort, tekst: v.systemPrompt || '' }],
        schema: null,
      };
    } catch (e) {
      alertDialog({ title: 'Fout', message: `Prompt laden mislukt: ${e.message}` });
    }
  }

  async _bekijkCodePrompt(id) {
    if (!this._prompts) {
      try { this._prompts = (await api.getAiPrompts())?.prompts || []; }
      catch (e) { alertDialog({ title: 'Fout', message: `Prompts laden mislukt: ${e.message}` }); return; }
    }
    const p = this._prompts.find((x) => x.id === id);
    if (!p) return;
    const rol = { system: 'Systeemprompt', user: 'Bericht met de invoer' };
    this._viewer = {
      title: p.title,
      sub: `${p.usedFor} Model nu: ${p.model}. Staat in de code; hier alleen te lezen. Tussen ‹ › staat wat per aanroep wordt ingevuld.`,
      blokken: p.messages.map((m) => ({ label: rol[m.role] || m.role, tekst: m.content })),
      schema: p.schema,
    };
  }

  _renderViewer() {
    const v = this._viewer;
    if (!v) return '';
    const sluit = () => { this._viewer = null; };
    return html`
      <div class="pv-back" @click=${(e) => { if (e.target === e.currentTarget) sluit(); }}
           @keydown=${(e) => { if (e.key === 'Escape') sluit(); }}>
        <div class="pv" role="dialog" aria-modal="true" aria-label=${v.title}>
          <div class="pv-head">
            <div>
              <h3>${v.title}</h3>
              <p class="hint" style="margin:2px 0 0;">${v.sub}</p>
            </div>
            <button class="pv-x" aria-label="Sluiten" @click=${sluit}>✕</button>
          </div>
          <div class="pv-body">
            ${v.blokken.map((b) => html`<h4>${b.label}</h4><pre>${b.tekst}</pre>`)}
            ${v.schema ? html`
              <details>
                <summary>Antwoordschema (JSON)</summary>
                <pre>${JSON.stringify(v.schema, null, 2)}</pre>
              </details>` : ''}
          </div>
        </div>
      </div>`;
  }

  /** Gebruikt een van de drie onderdelen een GPT-5/6-model? Dan doet de redeneerstand iets. */
  get _redeneertIets() {
    const d = this._draft || {};
    return [d.extractionModel, d.fieldOrderModel, d.labAssistantModel].some((m) => /^gpt-(5|6)/.test(m || ''));
  }

  /** Eén schakelaar met titel, ⓘ-uitleg en ondertitel. `aan` is de huidige stand. */
  _schakelaar(id, titel, uitleg, ondertitel, aan, onChange) {
    return html`
      <div class="toggle-rij">
        <div class="toggle-tekst">
          <label for=${id}>
            ${titel}
            <span data-tip=${uitleg} @mousemove=${tipMove} @mouseleave=${tipHide}
              style="display:inline-flex; vertical-align:-2px; color:var(--mv-text-sub,#6c757d); cursor:help;"
              >${unsafeSVG(lucideIcon(Info, 13))}</span>
          </label>
          <p class="hint" style="margin:0;">${ondertitel}</p>
        </div>
        <label class="schakelaar" title=${aan ? 'Aan' : 'Uit'}>
          <input id=${id} type="checkbox" role="switch" .checked=${aan} ?disabled=${this._saving}
            aria-checked=${aan ? 'true' : 'false'} @change=${(e) => onChange(e.target.checked)}>
          <span class="baan" aria-hidden="true"></span>
        </label>
      </div>`;
  }
  /** Een taal aan- of uitvinken. De volgorde volgt de lijst van de server, niet de klikvolgorde. */
  /** De talen die nog niet gekozen zijn, in de volgorde van de server. */
  get _resterendeTalen() {
    const gekozen = new Set(this._draft?.transcriptLanguages || []);
    return (this._options.transcriptLanguages || []).filter((c) => !gekozen.has(c));
  }

  _setTaal(code, aan) {
    const alle = this._options.transcriptLanguages || [];
    const nu = new Set(this._draft.transcriptLanguages || []);
    if (aan) nu.add(code); else nu.delete(code);
    this._draft = { ...this._draft, transcriptLanguages: alle.filter((c) => nu.has(c)) };
  }
  _setPrompt(profile, value) { this._draft = { ...this._draft, promptVersionIds: { ...(this._draft.promptVersionIds || {}), [profile]: value } }; }
  _setTranscriptPrompt(profile, value) { this._draft = { ...this._draft, transcriptionPromptVersionIds: { ...(this._draft.transcriptionPromptVersionIds || {}), [profile]: value } }; }

  async _save() {
    if (!this._dirty || this._saving) return;
    // Double confirmation — this changes production for every company.
    const c1 = await confirmDialog({
      title: 'Productie-instelling wijzigen',
      message: 'Let op: dit wijzigt het model/de prompt die voor ALLE gebruikers op /projects wordt gebruikt. Weet je het zeker?',
      confirmLabel: 'Doorgaan', danger: true,
    });
    if (!c1) return;
    const c2 = await confirmDialog({
      title: 'Definitief bevestigen',
      message: 'Tweede bevestiging: de nieuwe instelling gaat direct in productie voor alle bedrijven. Definitief toepassen?',
      confirmLabel: 'Ja, toepassen', danger: true,
    });
    if (!c2) return;
    this._saving = true;
    try {
      await api.setModelConfig({ ...this._draft, confirmed: true });
      await this._load();
      alertDialog({ title: 'Opgeslagen', message: 'De productie-config is bijgewerkt.' });
    } catch (e) {
      alertDialog({ title: 'Fout', message: e.message });
    } finally {
      this._saving = false;
    }
  }

  _toggleOpen(id) {
    const nu = new Set(this._open);
    if (nu.has(id)) nu.delete(id); else nu.add(id);
    this._open = nu;
  }

  /** Een keuzelijst met modellen. */
  _modelSelect(veld, modellen, standaard = '') {
    const d = this._draft || {};
    return html`
      <select class="model" @change=${(e) => this._set(veld, e.target.value)} ?disabled=${this._saving}>
        ${modellen.map((m) => html`<option value=${m} ?selected=${(d[veld] || standaard) === m}>${m}</option>`)}
      </select>`;
  }

  /**
   * Eén AI-onderdeel: nummer, naam met wat het doet, een samenvatting, de modelkeuze, en
   * (optioneel) een uitklapbaar deel met de instellingen.
   */
  _onderdeel({ id, badge, naam, wat, samenvatting, model, inhoud = null }) {
    const open = this._open.has(id);
    return html`
      <div class="onderdeel ${open ? 'open' : ''}">
        <div class="ond-rij">
          <span class="badge">${badge}</span>
          <div class="ond-tekst">
            <div class="ond-naam">${naam} <span class="ond-wat">${wat}</span></div>
            <div class="ond-sub">${samenvatting}</div>
          </div>
          ${model}
          ${inhoud ? html`
            <button class="klap" aria-expanded=${open ? 'true' : 'false'}
              aria-label="${open ? 'Instellingen dichtklappen' : 'Instellingen openklappen'}: ${naam}"
              @click=${() => this._toggleOpen(id)}>${unsafeSVG(lucideIcon(ChevronDown, 16))}</button>`
            : html`<span class="klap-plek"></span>`}
        </div>
        ${inhoud && open ? html`<div class="ond-inhoud">${inhoud}</div>` : ''}
      </div>`;
  }

  /** Eén promptkeuze in de tabel: keuzelijst, oogje, aantal versies en of er een nieuwere is. */
  _promptCel({ opts, sel, liveId, onChange, soort, metGeen }) {
    const live = opts.find((v) => v.id === liveId);
    // Is er een versie met een hoger nummer dan de gekozen? Op nummer en niet op aanmaaktijd: een
    // oudere versie kan later opnieuw zijn aangemaakt (bv. na het opruimen van dubbele versies).
    const gekozen = opts.find((v) => v.id === sel);
    const nieuwste = opts.reduce((best, v) => (vergelijkVersie(v.title, best?.title) > 0 ? v : best), null);
    const nieuwer = gekozen && nieuwste && nieuwste.id !== gekozen.id && vergelijkVersie(nieuwste.title, gekozen.title) > 0;
    return html`
      <div class="cel">
        <div class="cel-rij">
          <select @change=${(e) => onChange(e.target.value)} ?disabled=${this._saving || (!opts.length && !metGeen)}>
            ${metGeen ? html`<option value="" ?selected=${!sel}>— geen —</option>` : (opts.length ? '' : html`<option value="">— geen versie —</option>`)}
            ${opts.map((v) => html`<option value=${v.id} ?selected=${sel === v.id}>${v.title}</option>`)}
          </select>
          <button class="oog" title="Bekijken" aria-label="${soort} bekijken" ?disabled=${!sel}
            @click=${() => this._bekijkVersie(sel, soort)}>${unsafeSVG(lucideIcon(Eye, 15))}</button>
        </div>
        <div class="cel-meta">
          ${opts.length} versie${opts.length === 1 ? '' : 's'}
          ${nieuwer ? html`<span class="nieuw">${nieuwste.title.split(' ')[0]} beschikbaar</span>` : ''}
          ${live && sel !== liveId ? html`<span class="live">nu actief: ${live.title.split(' ')[0]}</span>` : ''}
        </div>
      </div>`;
  }

  render() {
    if (this._loading) return html`<p class="muted">Laden…</p>`;
    const d = this._draft || {};
    const tm = this._options.transcriptionModels || [];
    const em = this._options.extractionModels || [];
    const talen = d.transcriptLanguages || [];
    const woordenlijsten = EXTRACTION_PROFILES.filter((p) => (d.transcriptionPromptVersionIds || {})[p.id]).length;
    const gpt6 = /^gpt-(5|6)/.test(d.extractionModel || '');

    const transcriptieInhoud = html`
      <!--
        Toegestane talen. Komt er een transcript in een andere taal uit, dan is de
        transcriptie mislukt -- ook als hij vloeiend leest. Dan volgt er automatisch nog één
        poging met whisper-1, dat zich wél aan de opgegeven taal houdt.
      -->
      <div class="field">
        <label>Toegestane talen in een transcript</label>
        <div class="taalbalk">
          ${talen.map(code => html`
            <span class="taaltegel">
              ${TAALNAAM[code] || code}
              <button type="button" class="weg" ?disabled=${this._saving}
                title="${TAALNAAM[code] || code} verwijderen"
                aria-label="${TAALNAAM[code] || code} verwijderen"
                @click=${() => this._setTaal(code, false)}>&times;</button>
            </span>`)}
          ${this._resterendeTalen.length ? html`
            <select class="taaltoevoegen" ?disabled=${this._saving}
              .value=${''}
              @change=${(e) => { if (e.target.value) { this._setTaal(e.target.value, true); e.target.value = ''; } }}>
              <option value="">+ taal toevoegen</option>
              ${this._resterendeTalen.map(code => html`<option value=${code}>${TAALNAAM[code] || code}</option>`)}
            </select>` : ''}
          ${talen.length ? '' : html`<span class="leeg">Geen talen gekozen</span>`}
        </div>
        <p class="hint">
          ${talen.length
            ? html`Een transcript dat hier niet in past geldt als mislukt en wordt één keer
                   opnieuw geprobeerd met <code>whisper-1</code>.
                   ${talen.length === 1
                     ? html`Omdat er één taal gekozen is, krijgt het model die taal ook meteen opgelegd.`
                     : html`Met meerdere talen laten we het model zelf kiezen — die keuze kunnen we niet vooraf maken.`}`
            : html`Geen bewaking: elk transcript wordt geaccepteerd, in welke taal dan ook.`}
        </p>
      </div>
      <!--
        Hallucinatie preventie. Een transcriptiemodel antwoordt nooit 'niets': bij een opname met
        alleen wind schreef het een volledig boorprofiel uit. Met deze controle kijkt eerst
        een apart model (Silero VAD via Workers AI) of er een stem te horen is.
      -->
      <div class="field" style="margin-bottom:0;">
        ${this._schakelaar('hallucinatie-preventie', 'Hallucinatie preventie',
          `Transcriptiemodellen kunnen hallucineren door voorbeelden. Om dit tegen te gaan kijken we eerst hoeveel seconden er daadwerkelijk wordt gesproken in de opname. Vervolgens kijken we uit hoeveel woorden het transcript bestaat. Indien het aantal woorden per seconde boven ${this._options.maxWordsPerSecond ?? 5} ligt slaan we dit transcript over en verwerken we het boorprofiel niet. Daarnaast kijken we of het transcript een standaardtekst is die het model verzint als er niets gezegd wordt, zoals de voorbeeldtekst uit de woordenlijst of "Ondertitels ingediend door de Amara.org gemeenschap". Ook die transcripten slaan we over.`,
          'Hallucinatie tegengaan door logisch aantal gesproken woorden per seconde te hanteren.',
          !!d.speechCheck, (v) => this._set('speechCheck', v))}
      </div>`;

    const extractieInhoud = html`
      <!--
        Redeneerstand van een GPT-6-model (gpt-6-luna): hoeveel het model nadenkt voor het
        antwoord. Meer nadenken = nauwkeuriger, trager en iets duurder (de denkstappen tellen als
        uitvoer). Bij gpt-4.1 en gpt-4o doet hij niets. Validatieset 30-10-2026, gpt-6-luna:
        'none' 285 afwijkingen, 'low' 181 (gpt-4.1: 213).
      -->
      <div class="field">
        <label>Redeneerstand (alleen GPT-6)</label>
        <select @change=${(e) => this._set('reasoningEffort', e.target.value)}
                ?disabled=${this._saving || !this._redeneertIets}>
          ${(this._options.reasoningEfforts || ['none', 'low', 'medium', 'high']).map(r => html`
            <option value=${r} ?selected=${(d.reasoningEffort || 'low') === r}>${REDENEERSTAND[r] || r}</option>`)}
        </select>
        <small class="hint">${this._redeneertIets
          ? 'Meer nadenken is nauwkeuriger, maar trager en iets duurder. Op de validatieset deed "low" het beste. Geldt ook voor Vooronderzoek en Flip AI als die een GPT-6-model gebruiken.'
          : 'Alleen voor GPT-6-modellen; bij deze modellen heeft hij geen effect.'}</small>
      </div>
      <div class="field">
        ${this._schakelaar('compacte-uitvoer', 'Snelle uitvoer (compact)',
          'De extractie duurt vooral lang door de tekst die het model terugschrijft. Met deze instelling schrijft het model bij een meetpunt, laag of monster alleen de velden die het herkent, en vult de software de lege velden zelf aan. Gemeten: ongeveer de helft minder tokens en 1 à 2 seconden sneller per boring. Past het antwoord niet in het schema, dan wordt het automatisch nog één keer op de oude, volledige manier gevraagd. Test het eerst op /admin/validation.',
          'Het model schrijft alleen wat het herkent; de software vult de rest aan.',
          !!d.compactOutput, (v) => this._set('compactOutput', v))}
      </div>
      <div class="field" style="margin-bottom:0;">
        ${this._schakelaar('openai-priority', 'OpenAI priority',
          'OpenAI verwerkt de extractie met voorrang: een hoger en stabieler tempo, tegen een hogere prijs per token. Gemeten: ongeveer een kwart sneller. Werkt het account er niet mee, dan valt de software vanzelf terug op normale verwerking.',
          'Extractie met voorrang bij OpenAI: sneller, iets duurder.',
          !!d.priority, (v) => this._set('priority', v))}
      </div>`;

    return html`
      <div class="kop">
        <h1>AI & prompts</h1>
        <span class="prod">Productie · alle gebruikers op /projects</span>
      </div>
      <p class="intro">Welke modellen er draaien, met welke instellingen, en welke prompt er naar elk model gaat. Wijzigingen gaan pas live na bevestiging.</p>

      <!-- Alle plekken waar we een AI-model gebruiken, elk met zijn eigen keuze. -->
      <section class="card">
        <div class="card-head">
          <h2>AI-onderdelen</h2>
          <p class="card-sub">Elk onderdeel met zijn model. Klap open voor de instellingen.</p>
        </div>
        ${this._onderdeel({
          id: 'transcriptie', badge: '1', naam: 'Transcriptie', wat: 'audio → tekst',
          samenvatting: [
            talen.length ? talen.map((c) => TAALNAAM[c] || c).join(', ') : 'Geen taalbewaking',
            `Hallucinatiepreventie ${d.speechCheck ? 'aan' : 'uit'}`,
            `${woordenlijsten} van ${EXTRACTION_PROFILES.length} woordenlijsten actief`,
          ].join(' · '),
          model: this._modelSelect('transcriptionModel', tm),
          inhoud: transcriptieInhoud,
        })}
        ${this._onderdeel({
          id: 'extractie', badge: '2', naam: 'Extractie', wat: 'tekst → boorprofiel',
          samenvatting: [
            gpt6 ? `Redeneerstand ${(REDENEERSTAND[d.reasoningEffort || 'low'] || '').split(' ')[0].toLowerCase()}` : null,
            d.compactOutput ? 'Compact' : null,
            d.priority ? 'Priority' : null,
            `${EXTRACTION_PROFILES.length} extractieprompts`,
          ].filter(Boolean).join(' · '),
          model: this._modelSelect('extractionModel', em),
          inhoud: extractieInhoud,
        })}
        <!--
          Buiten de pijplijn van een opname. Zelfde lijst als de extractie; nooit ingesteld =
          gpt-4.1. De validatiepagina's meten deze niet: beoordeel een ander model in Analyse.
        -->
        ${this._onderdeel({
          id: 'vooronderzoek', badge: 'V', naam: 'Vooronderzoek', wat: 'veldwerkopdracht inlezen',
          samenvatting: html`Leest ook gescande opdrachten; kies een model dat afbeeldingen leest.
            <button class="link" @click=${() => this._bekijkCodePrompt('veldwerkopdracht')}>Prompt bekijken</button>
            <button class="link" @click=${() => this._bekijkCodePrompt('veldwerkopdracht-scan')}>Prompt scan</button>`,
          model: this._modelSelect('fieldOrderModel', em, 'gpt-4.1'),
        })}
        ${this._onderdeel({
          id: 'flip', badge: 'F', naam: 'Flip AI', wat: 'mengvoorstel en controle',
          samenvatting: html`Los van de opnames; per aanroep te zien in Analyse.
            <button class="link" @click=${() => this._bekijkCodePrompt('flip')}>Prompt bekijken</button>`,
          model: this._modelSelect('labAssistantModel', em, 'gpt-4.1'),
        })}
      </section>

      <!--
        Per analyse twee prompts: de woordenlijst die met de transcriptie meegaat (per analyse,
        want de termen verschillen; geen is een geldige keuze) en de extractieprompt. De actieve
        prompt is niet te bewerken: nieuwe versies maak je op de validatiepagina.
      -->
      <section class="card flush">
        <div class="card-head pad">
          <h2>Prompts per analyse</h2>
          <p class="card-sub">Per analyse gaan er twee prompts mee: de woordenlijst bij de transcriptie en de extractieprompt. Klik het oogje om de volledige tekst te zien. Nieuwe versies maak je op de validatiepagina van dat model.</p>
        </div>
        <div class="tabel" role="table">
          <div class="t-kop" role="row">
            <span role="columnheader">Analyse</span>
            <span role="columnheader">1 · Woordenlijst (transcriptie)</span>
            <span role="columnheader">2 · Extractieprompt</span>
          </div>
          ${EXTRACTION_PROFILES.map((p) => html`
            <div class="t-rij" role="row">
              <span class="t-naam" role="cell">${p.label}</span>
              <div role="cell">
                ${this._promptCel({
                  opts: this._versions.filter((v) => (v.profile || v.standard) === `transcriptie-${p.id}`),
                  sel: (d.transcriptionPromptVersionIds || {})[p.id] || '',
                  liveId: (this._config?.transcriptionPromptVersionIds || {})[p.id] || '',
                  onChange: (v) => this._setTranscriptPrompt(p.id, v),
                  soort: 'Woordenlijst', metGeen: true,
                })}
              </div>
              <div role="cell">
                ${this._promptCel({
                  opts: this._versions.filter((v) => normalizeExtractionProfile(v.profile || v.standard) === p.id),
                  sel: (d.promptVersionIds || {})[p.id],
                  liveId: (this._config?.promptVersionIds || {})[p.id],
                  onChange: (v) => this._setPrompt(p.id, v),
                  soort: 'Extractieprompt', metGeen: false,
                })}
              </div>
            </div>`)}
        </div>
      </section>

      <div class="actions ${this._dirty ? 'vuil' : ''}">
        ${this._dirty ? html`<span class="vuil-tekst">Niet opgeslagen wijzigingen</span>` : ''}
        ${this._dirty ? html`<button class="btn" @click=${() => this._draft = { ...this._config }} ?disabled=${this._saving}>Herstel</button>` : ''}
        <button class="btn primary" @click=${this._save} ?disabled=${!this._dirty || this._saving}>${this._saving ? 'Opslaan…' : 'Opslaan'}</button>
      </div>
      ${this._renderViewer()}
    `;
  }

  static styles = css`
    :host { display: block; max-width: 860px; }
    .kop { display: flex; align-items: center; flex-wrap: wrap; gap: .6rem; margin: 0 0 .3rem; }
    h1 { font-size: 1.4rem; margin: 0; color: var(--mv-text); }
    .prod { font-size: .72rem; font-weight: 600; color: #8a5a00; background: #fdf3dc; border: 1px solid #f3dca4;
      border-radius: 999px; padding: 2px 9px; white-space: nowrap; }
    .intro { margin: 0 0 1.25rem; font-size: .85rem; color: var(--mv-text-sub, #5e6278); max-width: 620px; line-height: 1.5; }
    .muted { color: var(--mv-text-muted); }
    .warn { background: #f6c34322; border: 1px solid #f6c34355; color: #8a6d1a; border-radius: .5rem; padding: .6rem .8rem; font-size: .85rem; margin-bottom: 1.25rem; }
    .card {
      border: 1px solid var(--mv-border, #eff2f5); border-radius: .75rem; background: var(--mv-surface, #fff);
      box-shadow: var(--mv-shadow, 0 8px 24px rgba(82,63,105,.06)); padding: 1.1rem 1.25rem 1.25rem; margin-bottom: 1.25rem;
    }
    .card-head { margin-bottom: .6rem; }
    .card.flush { padding: 0; overflow: hidden; }
    .card-head.pad { padding: 1.1rem 1.25rem .9rem; margin: 0; border-bottom: 1px solid var(--mv-border, #eff2f5); }

    /* AI-onderdelen: één rij per onderdeel, met badge, tekst, modelkeuze en uitklapper. */
    .onderdeel { border-top: 1px solid var(--mv-border, #eff2f5); }
    .onderdeel:first-of-type { border-top: 0; }
    .ond-rij { display: grid; grid-template-columns: 26px minmax(0, 1fr) 210px 28px; align-items: center; gap: 12px; padding: 12px 0; }
    .badge { width: 22px; height: 22px; border-radius: 6px; display: inline-flex; align-items: center; justify-content: center;
      font-size: .72rem; font-weight: 700; background: var(--mv-surface-alt, #f1f3f6); color: var(--mv-text-sub, #5e6278); }
    .ond-naam { font-size: .9rem; font-weight: 700; color: var(--mv-text); }
    .ond-wat { font-size: .74rem; font-weight: 500; color: var(--mv-text-muted, #a1a5b7); margin-left: 4px; }
    .ond-sub { font-size: .78rem; color: var(--mv-text-sub, #5e6278); margin-top: 2px; line-height: 1.45; }
    .ond-sub .link { margin-left: 6px; }
    .klap { background: none; border: none; cursor: pointer; color: var(--mv-text-muted); padding: 4px; display: inline-flex; border-radius: 6px; transition: transform .15s; }
    .klap:hover { background: var(--mv-surface-alt, #f1f3f6); }
    .onderdeel.open .klap { transform: rotate(180deg); }
    .ond-inhoud { padding: 4px 0 16px 38px; }
    select.model, .cel select {
      -webkit-appearance: none; appearance: none; width: 100%; padding: .5rem 2rem .5rem .7rem;
      border: 1px solid var(--mv-border, #e4e6ef); border-radius: .5rem; background-color: var(--mv-surface, #fff);
      color: var(--mv-text); font-size: .85rem; font-family: inherit; cursor: pointer; outline: none; text-overflow: ellipsis;
      background-image: ${unsafeCSS(CHEVRON)}; background-repeat: no-repeat; background-position: right 10px center; background-size: 13px;
    }
    select:disabled { opacity: .6; cursor: not-allowed; }

    /* Prompts per analyse: analyse | woordenlijst | extractieprompt. */
    .tabel { display: flex; flex-direction: column; }
    .t-kop, .t-rij { display: grid; grid-template-columns: 160px minmax(0, 1fr) minmax(0, 1fr); gap: 14px; padding: 12px 1.25rem; }
    .t-kop { padding-top: 10px; padding-bottom: 8px; border-bottom: 1px solid var(--mv-border, #eff2f5);
      font-size: .68rem; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; color: var(--mv-text-muted, #a1a5b7); }
    .t-rij + .t-rij { border-top: 1px solid var(--mv-border, #eff2f5); }
    .t-naam { font-size: .85rem; font-weight: 700; color: var(--mv-text); padding-top: 8px; }
    .cel-rij { display: flex; gap: 6px; }
    .oog { flex: none; width: 34px; border: 1px solid var(--mv-border, #e4e6ef); border-radius: .5rem; background: var(--mv-surface, #fff);
      color: var(--mv-text-sub, #5e6278); display: inline-flex; align-items: center; justify-content: center; cursor: pointer; }
    .oog:hover:not(:disabled) { color: var(--mv-primary, #4b9d35); border-color: var(--mv-primary, #4b9d35); }
    .oog:disabled { opacity: .4; cursor: not-allowed; }
    .cel-meta { font-size: .72rem; color: var(--mv-text-muted, #a1a5b7); margin-top: 4px; display: flex; gap: 8px; flex-wrap: wrap; }
    .cel-meta .nieuw { color: var(--mv-primary, #4b9d35); font-weight: 600; }
    .cel-meta .live { color: #b45309; }

    @media (max-width: 760px) {
      .ond-rij { grid-template-columns: 26px minmax(0, 1fr) 28px; }
      .ond-rij select.model { grid-column: 2 / 3; grid-row: 2; }
      .ond-inhoud { padding-left: 0; }
      .t-kop { display: none; }
      .t-rij { grid-template-columns: minmax(0, 1fr); }
    }
    .card-head h2 { font-size: 1rem; margin: 0; color: var(--mv-text); }
    /* Invoerbalk met tegels: gekozen talen staan als pil, met een kruisje om ze weg te klikken. */
    .taalbalk {
      display: flex; flex-wrap: wrap; align-items: center; gap: 6px;
      min-height: 38px; padding: 5px 8px;
      border: 1px solid var(--mv-border, #e4e6ef); border-radius: 8px;
      background: var(--mv-surface, #fff);
    }
    .taaltegel {
      display: inline-flex; align-items: center; gap: 4px;
      padding: 3px 4px 3px 10px; border-radius: 999px;
      background: var(--mv-primary-wash, #e9f1ea); color: var(--mv-primary-ink, #0d5c2b);
      font-size: .82rem; font-weight: 600; white-space: nowrap;
    }
    .taaltegel .weg {
      border: 0; background: none; cursor: pointer; line-height: 1;
      font-size: 1.05rem; padding: 0 4px; border-radius: 999px;
      color: inherit; opacity: .6;
    }
    .taaltegel .weg:hover:not(:disabled) { opacity: 1; background: rgba(0,0,0,.08); }
    .taaltegel .weg:disabled { cursor: default; opacity: .3; }
    .taaltoevoegen {
      border: 0; background: none; color: var(--mv-text-sub, #5e6278);
      font-size: .82rem; cursor: pointer; padding: 2px 4px;
    }
    .taalbalk .leeg { font-size: .82rem; color: var(--mv-text-muted, #6c757d); }
    .hint { margin: 6px 0 0; font-size: 0.82rem; color: var(--mv-text-muted, #6c757d); line-height: 1.5; }
    .card-sub { font-size: .8rem; color: var(--mv-text-sub, #5e6278); margin: .2rem 0 0; }
    .field { margin-bottom: 1.1rem; display: flex; flex-direction: column; gap: .35rem; }
    .toggle-rij { display: flex; align-items: center; justify-content: space-between; gap: 1rem; }
    .toggle-tekst { display: flex; flex-direction: column; gap: .35rem; min-width: 0; }
    .schakelaar { position: relative; display: inline-flex; flex: none; cursor: pointer; }
    .schakelaar input { position: absolute; opacity: 0; width: 100%; height: 100%; margin: 0; cursor: pointer; }
    .schakelaar .baan { position: relative; width: 40px; height: 22px; border-radius: 999px; background: var(--mv-border, #d6d9e3); transition: background .15s; }
    .schakelaar .baan::after { content: ''; position: absolute; top: 3px; left: 3px; width: 16px; height: 16px; border-radius: 50%; background: #fff; box-shadow: 0 1px 2px rgba(0,0,0,.25); transition: transform .15s; }
    .schakelaar input:checked + .baan { background: var(--mv-primary, #1b84ff); }
    .schakelaar input:checked + .baan::after { transform: translateX(18px); }
    .schakelaar input:focus-visible + .baan { outline: 2px solid var(--mv-primary, #1b84ff); outline-offset: 2px; }
    .schakelaar input:disabled + .baan { opacity: .5; }
    .field label { font-size: .8rem; font-weight: 700; color: var(--mv-text-sub, #5e6278); display: flex; align-items: baseline; gap: .4rem; }
    .count { font-weight: 500; font-size: .72rem; color: var(--mv-text-muted, #a1a5b7); }
    .live { font-size: .72rem; color: #b45309; }
    .field select {
      -webkit-appearance: none; appearance: none; padding: .55rem 2rem .55rem .7rem;
      border: 1px solid var(--mv-border, #eff2f5); border-radius: .5rem; background-color: var(--mv-surface, #fff);
      color: var(--mv-text); font-size: .9rem; font-family: inherit; cursor: pointer; outline: none;
      background-image: ${unsafeCSS(CHEVRON)}; background-repeat: no-repeat; background-position: right 10px center; background-size: 13px;
    }
    .hint { font-size: .75rem; color: var(--mv-text-muted); margin: .1rem 0 0; }
    .actions { display: flex; align-items: center; justify-content: flex-end; gap: .6rem; margin-top: 1.25rem; }
    .actions.vuil { position: sticky; bottom: 12px; background: var(--mv-surface, #fff); border: 1px solid var(--mv-border, #eff2f5);
      border-radius: .75rem; padding: .6rem .75rem; box-shadow: 0 8px 24px rgba(15,23,42,.12); }
    .vuil-tekst { margin-right: auto; font-size: .82rem; font-weight: 600; color: #b45309; }
    .btn { padding: .55rem 1.1rem; border-radius: .5rem; border: 1px solid var(--mv-border, #eff2f5); background: var(--mv-surface, #fff); color: var(--mv-text); font-weight: 600; cursor: pointer; font-size: .9rem; }
    .btn.primary { background: var(--mv-primary, #4b9d35); color: #fff; border-color: transparent; }
    .btn:disabled { opacity: .5; cursor: not-allowed; }
    .prompt-links { display: flex; gap: 14px; margin-top: 6px; }
    .link { background: none; border: none; padding: 0; font: inherit; font-size: .82rem; font-weight: 600; color: var(--mv-primary, #4b9d35); cursor: pointer; }
    .link:hover { text-decoration: underline; }
    .pv-back { position: fixed; inset: 0; z-index: var(--mv-z-modal, 1200); background: rgba(15, 23, 42, .45);
      display: flex; align-items: center; justify-content: center; padding: 4vh 1rem; }
    .pv { width: min(860px, 100%); max-height: 92vh; background: var(--mv-surface, #fff); color: var(--mv-text); border-radius: .8rem;
      box-shadow: 0 24px 60px rgba(0,0,0,.25); display: flex; flex-direction: column; overflow: hidden; }
    .pv-head { display: flex; align-items: flex-start; gap: 12px; padding: 22px 24px 12px; border-bottom: 1px solid var(--mv-border, #eff2f5); }
    .pv-head h3 { margin: 0; font-size: 1rem; font-weight: 800; }
    .pv-x { margin-left: auto; background: none; border: none; cursor: pointer; color: var(--mv-text-muted); font-size: 1rem; padding: 4px; }
    .pv-body { padding: 8px 24px 20px; overflow: auto; }
    .pv-body h4 { margin: 14px 0 6px; font-size: .8rem; text-transform: uppercase; letter-spacing: .04em; color: var(--mv-text-muted); }
    .pv-body pre { margin: 0; white-space: pre-wrap; word-break: break-word; font-size: .8rem; line-height: 1.5;
      background: var(--mv-surface-alt, #f6f8fa); border: 1px solid var(--mv-border, #eff2f5); border-radius: .5rem; padding: 12px 14px; }
    .pv-body details { margin-top: 14px; }
    .pv-body summary { cursor: pointer; font-size: .82rem; font-weight: 600; margin-bottom: 6px; }
  `;
}

customElements.define('admin-model-settings', AdminModelSettings);
